import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_NAVBAR_HEIGHT = 75;

export const useStickyTableHeader = (refreshDeps = []) => {
  const tableWrapRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const theadRef = useRef(null);
  const fixedHeaderTableRef = useRef(null);
  const [tableScrollLeft, setTableScrollLeft] = useState(0);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [fixedHeaderMetrics, setFixedHeaderMetrics] = useState({
    top: 0,
    left: 0,
    width: 0,
    tableWidth: 0,
  });

  const syncFixedHeaderColumnWidths = useCallback(() => {
    const sourceCells = theadRef.current?.querySelectorAll("th");
    const fixedCells = fixedHeaderTableRef.current?.querySelectorAll("th");
    if (!sourceCells?.length || !fixedCells?.length) {
      return;
    }

    sourceCells.forEach((sourceCell, index) => {
      const fixedCell = fixedCells[index];
      if (!fixedCell) {
        return;
      }

      const width = sourceCell.getBoundingClientRect().width;
      fixedCell.style.width = `${width}px`;
      fixedCell.style.minWidth = `${width}px`;
    });
  }, []);

  const updateFixedHeader = useCallback(() => {
    const wrap = tableWrapRef.current;
    const thead = theadRef.current;
    const scrollContainer = tableScrollRef.current;
    const table = tableRef.current;
    if (!wrap || !thead || !scrollContainer || !table) {
      return;
    }

    const navbar = document.querySelector(".layout-navbar");
    const navbarHeight =
      navbar?.getBoundingClientRect().height ?? DEFAULT_NAVBAR_HEIGHT;
    const wrapRect = wrap.getBoundingClientRect();
    const theadHeight = thead.getBoundingClientRect().height;
    const shouldFix =
      wrapRect.top <= navbarHeight &&
      wrapRect.bottom > navbarHeight + theadHeight;

    setIsHeaderFixed(shouldFix);

    if (shouldFix) {
      setFixedHeaderMetrics({
        top: navbarHeight,
        left: wrapRect.left,
        width: wrapRect.width,
        tableWidth: table.scrollWidth,
      });
      requestAnimationFrame(syncFixedHeaderColumnWidths);
    }
  }, [syncFixedHeaderColumnWidths]);

  const handleTableScroll = (event) => {
    setTableScrollLeft(event.currentTarget.scrollLeft);
    requestAnimationFrame(updateFixedHeader);
  };

  useEffect(() => {
    const handleUpdate = () => requestAnimationFrame(updateFixedHeader);

    handleUpdate();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...refreshDeps, tableScrollLeft, updateFixedHeader]);

  useEffect(() => {
    if (isHeaderFixed) {
      requestAnimationFrame(syncFixedHeaderColumnWidths);
    }
  }, [isHeaderFixed, fixedHeaderMetrics, syncFixedHeaderColumnWidths]);

  return {
    tableWrapRef,
    tableScrollRef,
    tableRef,
    theadRef,
    fixedHeaderTableRef,
    tableScrollLeft,
    isHeaderFixed,
    fixedHeaderMetrics,
    handleTableScroll,
  };
};
